import { Avatar, AvatarGroup, Box, Button, Card, CardContent, CardHeader, Grid, IconButton, Menu, MenuItem, Typography, Chip, Divider } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useContext, useEffect, useMemo, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { getForum } from "../../controllers/forum/forum"
import { useNavigate, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"
import { MoreVert, AccessTime, Person, Reply } from "@mui/icons-material"
import { CategoryDisplay } from "../../components/category.display"
import SlimCommentDrawer from "../../components/post/SlimCommentDrawer"
import { getAccount } from "../../controllers/accounts/accounts.js"
import { PostImages } from "../../components/post_images.widget"
import { getSinglePost } from "../../controllers/posts/posts"

export const ShowForum = () => {
    const [leaders, setLeaders] = useState([])
    const { id } = useParams()
    const { userDetails } = useContext(UserContext)
    let token = userDetails?.token
    const { data:forum, refetch } = useQuery({ queryKey: ['getSingleForum'], queryFn: () => getForum(token, id) })
    
    let single_forum = useMemo(() => forum?.forum ?? {}, [forum?.forum]) 

    // Get the forum post content
    const { data:content, refetch:refetchPost } = useQuery({ 
        queryKey: ['getSinglePost', single_forum?.status], 
        queryFn: () => getSinglePost(single_forum?.status, token),
        enabled: !!single_forum?.status
    })

    useEffect(() => {
        refetch()
        if (single_forum?.status) {
            refetchPost()
        }
    }, [single_forum?.status])

    useEffect(() => {
      setLeaders([])
      single_forum?.author?.forEach(async (member) => {
          const acct = await getAccount(member, token)
          setLeaders(ids => [...ids, acct])
      })
    }, [single_forum, token])

    let navigate = useNavigate()
    useEffect(() => {
        refetch()
    })

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
        <MainLoggedInLayout>
            <Grid 
                container 
                spacing={{ xs: 1, sm: 2, md: 3 }} 
                sx={{ 
                    pl: { xs: 0, sm: 2 }, 
                    pr: { xs: 1, sm: 2 },
                    '& > .MuiGrid-item': {
                        paddingLeft: { xs: '0px !important', sm: '16px !important', md: '24px !important' }
                    }
                }}
            >
                {/* Main Forum Card */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title={
                                <Typography variant="h5" component="h1" sx={{ wordBreak: 'break-word' }}>
                                    {single_forum?.title?.split("Original post: ")[0]}
                                </Typography>
                            }
                            action={
                                single_forum?.author?.includes(userDetails?.id) && (
                                    <div>
                                        <IconButton
                                          aria-label="more"
                                          id="long-button"
                                          aria-controls={openMenu ? 'long-menu' : undefined}
                                          aria-expanded={openMenu ? 'true' : undefined}
                                          aria-haspopup="true"
                                          onClick={handleClick}
                                        >
                                          <MoreVert />
                                        </IconButton>
                                        <Menu
                                          id="long-menu"
                                          MenuListProps={{
                                            'aria-labelledby': 'long-button',
                                          }}
                                          anchorEl={anchorEl}
                                          open={openMenu}
                                          onClose={handleClose}
                                        >
                                            <MenuItem onClick={handleClose}>
                                                <Button variant="contained" size="small" onClick={() => navigate("/forum/edit/"+single_forum?._id)}>Edit</Button>
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                )
                            }
                        />
                        <CardContent>
                            <Box sx={{ mb: 2 }}>
                                <CategoryDisplay label={single_forum?.category} />
                            </Box>
                            
                            {/* Forum metadata */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                {single_forum?.created_at && (
                                    <Chip 
                                        icon={<AccessTime />}
                                        label={new Date(single_forum.created_at).toLocaleDateString()}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Forum Content */}
                {content?.content && (
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader 
                                title={
                                    <Typography variant="h6" fontWeight="bold">
                                        Forum Content
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.6,
                                        '& p': { mb: 2 },
                                        '& a': { color: 'primary.main', textDecoration: 'underline' }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: content.content }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Media Section */}
                {(forum?.media_attachments?.length > 0 || content?.media_attachments?.length > 0) && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    Attachments
                                </Typography>
                                <PostImages imgs={[
                                    ...(forum?.media_attachments ?? []),
                                    ...(content?.media_attachments ?? [])
                                ]} />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Original Post Link Section */}
                {single_forum?.title?.includes("Original post: ") && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom fontWeight="bold">
                                    Source
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <a href={single_forum?.title?.split("Original post: ")[1]} target="_blank" rel="noopener noreferrer">
                                        <Button fullWidth variant="outlined" size="large">
                                            View Original {single_forum?.title?.includes('quora') ? "Quora " : ""}Post
                                        </Button>
                                    </a>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Forum Details Section */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Forum Details
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Created
                                </Typography>
                                <Typography variant="body2">
                                    {single_forum?.created_at ? new Date(single_forum.created_at).toLocaleString() : 'Unknown'}
                                </Typography>
                            </Box>

                            {single_forum?.updated_at && single_forum.updated_at !== single_forum.created_at && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body2">
                                        {new Date(single_forum.updated_at).toLocaleString()}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Category
                                </Typography>
                                <CategoryDisplay label={single_forum?.category} hideLabel={false} />
                            </Box>

                            {single_forum?.community_id && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Community
                                    </Typography>
                                    <Button 
                                        variant="outlined" 
                                        size="small"
                                        onClick={() => navigate("/community/show/" + single_forum.community_id)}
                                    >
                                        View Community
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Author Section */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Author
                            </Typography>
                            <AvatarGroup sx={{ justifyContent: 'flex-start' }}>
                                {leaders.map((leader, i) =>
                                    <Avatar 
                                        key={i} 
                                        onClick={() => navigate("/profile/"+leader?.id)} 
                                        alt="Author" 
                                        src={leader?.avatar} 
                                        sx={{ cursor: 'pointer', width: 56, height: 56 }}
                                    />
                                )}
                            </AvatarGroup>
                            {leaders.length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Click to view profile
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Discussion Stats Section */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Discussion
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Reply sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="h4" fontWeight="bold" color="primary.main">
                                    {forum?.replies_count || 0}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {forum?.replies_count === 1 ? 'Reply' : 'Replies'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Comments Section */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                Join the Discussion
                            </Typography>
                            <SlimCommentDrawer
                                count={forum?.replies_count}
                                token={token}
                                id={forum?.id}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </MainLoggedInLayout>
    )
}